/**
 * Skill-slug parsing for the chat composer + chat-history renderer.
 *
 * Inline notation embedded in user message text:
 * `/<name>[(private|org|org-editable)][@v<N>]`.
 * Parsed client-side by the chat composer (for autocomplete) and the history
 * renderer (for chip highlights). The server stores the message text
 * verbatim and never parses this syntax; the structured `invoke_skills`
 * field on `SendMessageRequest` / `StartAgentSessionRequest` is what
 * actually drives invocation on the wire.
 *
 * The bare form (`/foo`) resolves with private-wins tie-break; the explicit
 * form (`/foo(org)`) pins a specific accessibility variant. The autocomplete
 * only emits the `(private)` / `(org)` suffix when two visible skills share
 * the name; bare `/foo` stays uncluttered in the common case.
 *
 * Anchoring: matches must be preceded by start-of-string or a whitespace
 * character, so URLs like `https://example.com/foo` don't accidentally get
 * picked up. Following characters are stopped at the first non-slug character
 * (i.e., the slug ends where it ends).
 */

import { SkillAccessibility, SkillRecord, SkillSummary } from "./SkillRecord";

/**
 * Single regex used everywhere — kept exported so callers can construct their
 * own matchers (e.g., a highlight renderer that needs match indices, an
 * autocomplete that needs an in-progress slug, …) without inventing variants.
 *
 * Group 1: the skill name (alphanumerics, underscore, hyphen).
 * Group 2: optional accessibility disambiguator (`private`, `org`, or
 *   `org-editable`), captured without the surrounding parens. `org-editable`
 *   precedes `org` in the alternation so the longer token is preferred.
 * Group 3: optional version digits (without the `v` prefix).
 *
 * Anchor is a non-capturing lookbehind for start-of-string or whitespace; the
 * `g` flag and `lastIndex` discipline are the caller's responsibility.
 */
export const SLUG_REGEX =
  /(?:^|(?<=\s))\/([A-Za-z0-9_-]+)(?:\((private|org-editable|org)\))?(?:@v(\d+))?/g;

export interface ParsedSlug {
  /** Skill name as it appears in the text (case may differ from the canonical record). */
  name: string;
  /**
   * Explicit accessibility pin if the slug used ``(private)`` / ``(org)``; null
   * when the user wrote just ``/name``. Only emitted by the autocomplete when
   * two visible skills share the name — bare slugs default to private on
   * collision via :func:`resolveSlugs`.
   */
  accessibility: SkillAccessibility | null;
  /** Explicit version pin if the slug used `@vN`; null when the user wrote just `/name`. */
  version: number | null;
  /** Character offset of the leading `/` in the source text. */
  start: number;
  /** Character offset one past the slug's last character. */
  end: number;
}

/** Parse every well-anchored slug from the message text, in source order. */
export function parseSlugs(text: string): ParsedSlug[] {
  const slugs: ParsedSlug[] = [];
  // Allocate a fresh regex per call so concurrent callers don't share lastIndex
  // state. The compiled regex above is fine for one-shot test() / match() calls
  // but a for-of over matchAll() also resets per call, so the local clone is
  // belt-and-suspenders.
  const re = new RegExp(SLUG_REGEX.source, SLUG_REGEX.flags);
  for (const match of text.matchAll(re)) {
    const fullLength = match[0].length;
    const startOfFullMatch = match.index ?? 0;
    // The `/` is always at the start of the captured slug. When the match
    // started with a leading whitespace (because of `(?:^|(?<=\s))`), the
    // lookbehind variant we use here is zero-width — match[0] does not include
    // the whitespace. Both branches therefore have the same `/` offset.
    const start = startOfFullMatch;
    const end = start + fullLength;
    const accessibilityStr = match[2];
    const versionStr = match[3];
    slugs.push({
      name: match[1],
      accessibility:
        accessibilityStr === undefined
          ? null
          : (accessibilityStr as SkillAccessibility),
      version: versionStr === undefined ? null : Number(versionStr),
      start,
      end,
    });
  }
  return slugs;
}

export interface ResolvedSlug extends ParsedSlug {
  /** The Personal-tab skill record this slug matched, or null if the name didn't resolve. */
  skill: SkillRecord | null;
}

/**
 * Resolve parsed slugs against a Personal-tab summary list.
 *
 * Lookup is case-insensitive. A slug with an explicit ``(private)`` / ``(org)``
 * suffix matches that exact accessibility variant; a bare slug defaults to the
 * caller's private entry when both variants share a name (matching the tie-break
 * in ``SkillsRepo.get_skill_by_name``).
 */
export function resolveSlugs(
  parsed: ParsedSlug[],
  skills: readonly SkillSummary[],
): ResolvedSlug[] {
  // Exact lookup keyed by ``(name, accessibility)`` — used when the slug
  // carries an explicit disambiguator.
  const byNameAccess = new Map<string, SkillSummary>();
  // Default-on-collision lookup keyed by name only — private wins.
  const byName = new Map<string, SkillSummary>();
  for (const summary of skills) {
    const nameKey = summary.skill.name.toLowerCase();
    byNameAccess.set(`${nameKey}:${summary.skill.accessibility}`, summary);
    const incumbent = byName.get(nameKey);
    // Private wins on a name collision — the org-visible variant (`org` or
    // `org-editable`) yields to the caller's own private skill of that name.
    if (
      incumbent === undefined ||
      (incumbent.skill.accessibility !== "private" &&
        summary.skill.accessibility === "private")
    ) {
      byName.set(nameKey, summary);
    }
  }
  return parsed.map((slug) => {
    const nameKey = slug.name.toLowerCase();
    const summary =
      slug.accessibility === null
        ? byName.get(nameKey)
        : byNameAccess.get(`${nameKey}:${slug.accessibility}`);
    return { ...slug, skill: summary?.skill ?? null };
  });
}

/** Active in-progress slug at `cursorPosition`, suitable for driving an autocomplete popper. */
export interface InProgressSlug {
  /** Substring after the `/`, used as the autocomplete filter. */
  query: string;
  /** Offset of the leading `/` — autocomplete replaces from here to `cursorPosition`. */
  tokenStart: number;
}

/**
 * If the caret sits inside a slug being typed (immediately after `/` or after
 * additional slug characters), return the in-progress info so the composer can
 * surface autocomplete. Returns null otherwise.
 *
 * Definition of "inside a slug": walking backward from the caret, every
 * character up to (and including) a `/` is a valid slug-name character, and
 * that `/` is preceded by start-of-string or whitespace. The caret can sit
 * anywhere in the run — clicking into the middle of `/qa-review` opens the
 * autocomplete prefilled with the prefix up to the caret (intentionally,
 * so users can re-pick a different skill on an existing slug). Only the
 * substring `/` → caret is returned as the query; characters to the right
 * of the caret are not consulted.
 */
export function activeSlugAtCursor(
  text: string,
  cursorPosition: number,
): InProgressSlug | null {
  if (cursorPosition < 1) {
    return null;
  }
  // Walk back from the caret looking for the `/` that started this slug. Stop
  // at the first non-slug character — if we hit whitespace or a non-allowed
  // character without finding a `/`, there's no active slug here.
  let i = cursorPosition - 1;
  while (i >= 0) {
    const ch = text.charAt(i);
    if (ch === "/") {
      // Anchor check: the char before `/` must be start-of-string or whitespace.
      if (i === 0 || /\s/.test(text.charAt(i - 1))) {
        return { query: text.slice(i + 1, cursorPosition), tokenStart: i };
      }
      return null;
    }
    if (!/[A-Za-z0-9_-]/.test(ch)) {
      return null;
    }
    i -= 1;
  }
  return null;
}
